import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore.js';
import { useBrandingStore, useFeatureEnabled, useFeatures } from './stores/brandingStore.js';
import { OPERATIONS, hasAdminAccess } from './components/layout/navItems.js';
import { isVisible } from './components/layout/navVisibility.js';
import {
  AdminDashboardPage,
  CombinedDashboardPage,
  PersonalDashboardPage,
} from './lazyPages.js';

/* ===========================================================================
 * THE THREE ROUTES THAT DECIDE WHICH PAGE THEY ARE.
 *
 * Lifted out of `App.tsx`, which is the route TABLE. These three are the
 * exceptions to a table: each renders a different page depending on the
 * tenant's profile, its features or the reader's role, and the reasoning for
 * each — why the staff importer is wrong for a construction tenant, why
 * everybody lands on their own workspace, why the admin landing is never a
 * dead end — belongs with the code rather than in a routing file.
 * ======================================================================== */

/**
 * Route guard for the staff "Bulk import" (/employees/import). That importer is
 * the login-based onboarding flow (it needs emails), which is wrong for the
 * construction profile — login-less site workers come from the CLI worker /
 * site-master import instead. So when the tenant runs the construction
 * terminology profile we hide the nav item *and* redirect the URL home, so the
 * page can't be reached by typing it. There is no "import" feature flag to gate
 * on, so this guards on the profile directly. Fail-open: an absent/legacy
 * terminology (non-construction) renders the page as before.
 */
export function NonConstructionRoute({ children }: { children: ReactNode }): JSX.Element {
  const isConstruction = useBrandingStore(
    (s) => s.branding?.terminology?.profile === 'construction'
  );
  if (isConstruction) return <Navigate to="/employees" replace />;
  return <>{children}</>;
}

/**
 * Home ("/"). Everyone — admins included — lands on their personal workspace
 * dashboard (their "my day": check-in, my shift, my pending leave). Company
 * management lives behind the separate Admin panel. On a management-only console
 * (self-service off) there's no personal/admin split, so fall back to the
 * combined dashboard that shows everything the role can see.
 */
export function HomeRoute(): JSX.Element {
  const selfService = useFeatureEnabled('selfService');
  return selfService ? <PersonalDashboardPage /> : <CombinedDashboardPage />;
}

/**
 * Admin-panel landing ("/overview") — the workforce/management overview a user
 * sees after opening the Admin panel. Anyone without management access is
 * redirected back to their workspace, so it's never a dead end for a
 * rank-and-file employee who lands on the URL directly.
 */
export function AdminOverviewRoute(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const permissions = useAuthStore((s) => s.user?.permissions);
  const features = useFeatures();
  if (!hasAdminAccess(role, features, permissions)) return <Navigate to="/" replace />;
  /* ONE PAGE NOW (2026-10-15): the figures live over the map at /operations.
     Only a role that may not see the floor still gets this page's decisions. */
  if (isVisible(OPERATIONS, role, features, permissions)) return <Navigate to="/operations" replace />;
  return <AdminDashboardPage />;
}
